import type { Pagination, ResetPage, SetPage, SetPageSize } from '@app/src/types/filter'
import { DEFAULT_TABLE_PAGE_SIZE } from '@app/src/wf-constants'
import qs from 'qs'
import { useCallback, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { useBetterQueryParams } from './queryParams'
import { useQueryState } from './queryState'

const INITIAL_STATE = {
  page: 1,
  pageSize: DEFAULT_TABLE_PAGE_SIZE,
}

/**
 * Pagination hook that persists state in URL query parameters.
 * This allows pagination state to survive browser back/forward navigation.
 *
 * Use this instead of usePagination when you want pagination state to persist
 * across navigation (e.g., clicking an item and then going back).
 *
 * @param initialState - Initial pagination state
 * @param filterDependencies - Optional array of filter values that should reset pagination to page 1 when changed.
 *                              Can include arrays/objects - they will be automatically serialized for comparison.
 *
 * @example
 * // Auto-reset when filters change
 * const filters = useGetApiQueryFilters(allowedFilters)
 * const [page, pageSize, setPage, setPageSize, resetPage] = usePaginationWithQuery(undefined, [filters])
 *
 * @example
 * // Auto-reset when multiple dependencies change
 * const [page, pageSize, setPage, setPageSize] = usePaginationWithQuery(
 *   { page: 1, pageSize: 50 },
 *   [periodFilter, levelFilter]
 * )
 */
const usePaginationWithQuery = (
  initialState: Pagination = INITIAL_STATE,
  filterDependencies?: unknown[],
): [number, number, SetPage, SetPageSize, ResetPage] => {
  const history = useHistory()
  const params = useBetterQueryParams()
  const [pageParam, setPageParam] = useQueryState<number>('page', initialState.page)
  const [pageSizeParam] = useQueryState<number>('pageSize', initialState.pageSize)

  // Parse query params to integers (qs.parse returns strings, but they're typed as numbers)
  const page = Math.max(1, parseInt(String(pageParam)) || initialState.page)
  const pageSize = Math.max(1, parseInt(String(pageSizeParam)) || initialState.pageSize)

  const setPage: SetPage = useCallback(
    (newPage: number) => {
      setPageParam(Math.max(1, Math.floor(newPage)), true)
    },
    [setPageParam],
  )

  const setPageSize: SetPageSize = useCallback(
    (newPageSize: number) => {
      const validPageSize = Math.max(1, Math.floor(newPageSize))
      // Update both page and pageSize in one operation to avoid race condition
      const queryString = qs.stringify({ ...params, page: 1, pageSize: validPageSize }, { encodeValuesOnly: true })
      history.replace(`${window.location.pathname}?${queryString}`)
    },
    [history, params],
  )

  const resetPage = useCallback(() => {
    setPageParam(1, true)
  }, [setPageParam])

  const prevSerializedRef = useRef<string | undefined>(undefined)

  // Auto-reset to page 1 when filters change (but not on initial mount)
  useEffect(() => {
    if (!filterDependencies) return

    const serialized = JSON.stringify(filterDependencies)

    if (prevSerializedRef.current === undefined) {
      prevSerializedRef.current = serialized
      return
    }

    if (prevSerializedRef.current !== serialized) {
      prevSerializedRef.current = serialized
      resetPage()
    }
  }, [filterDependencies, resetPage])

  return [page, pageSize, setPage, setPageSize, resetPage]
}

export default usePaginationWithQuery
